import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceDot
} from 'recharts';
import { getDrugById, getDrugColor } from '../data/drugs';
import { useDrugs } from '../contexts/DrugsContext';

// TypeScript interfaces
export interface Dose {
  id: string;
  drugId: string;
  timestamp: number; // milliseconds since epoch
  dosage: number;
  unit: string;
}

interface DrugParams {
  timeToPeak: number; // minutes
  totalDuration: number; // minutes
  drugName?: string; // Name of the drug
  color?: string; // Color for the drug line
  unit?: string; // Unit of measurement
  isFastActing?: boolean; // Whether the drug is fast-acting (for time intervals)
}

interface DataPoint {
  time: number; // minutes since first dose
  timeLabel: string; // formatted time (HH:MM)
  [key: string]: number | string; // can be drug IDs or dose IDs (format: drugId_doseId)
}

interface DrugEffectData {
  drugId: string;
  drugName: string;
  color: string;
  maxEffect: number;
  unit: string;
  isFastActing: boolean;
  dataPoints: DataPoint[];
  doses: Dose[]; // Include the doses for rendering individual lines
}

// Helper function to determine if a drug is fast-acting (for time intervals)
const isFastActingDrug = (drugId: string): boolean => {
  // List of drugs that are typically fast-acting (onset < 15 min)
  const fastActingDrugs = [
    'cocaine', 'amphetamine', '3mmc', 'dmt', 'changa', 'caffeine'
  ];
  return fastActingDrugs.includes(drugId);
};

// Custom tooltip to show formatted time and effect values with units
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Get the actual data point for proper time display
    const point = payload[0]?.payload;
    const timeLabel = point?.timeLabel || label;
    
    // Find the total line (the one without underscore in the dataKey)
    const totalEntry = payload.find((entry: any) => !entry.dataKey.includes('_'));
    
    if (!totalEntry) return null;
    
    // Get the total value and unit
    const totalValue = Math.round(totalEntry.value * 100) / 100;
    const unit = totalEntry.unit || '';
    
    return (
      <div className="bg-gray-800 border border-gray-700 rounded p-2 shadow-lg text-sm">
        <p className="font-bold text-gray-200">{`Effective@${timeLabel} → ${totalValue}${unit}`}</p>
      </div>
    );
  }
  return null;
};

// Function to calculate the effect of a single dose at a given time
const calculateDoseEffect = (
  elapsedMinutes: number,
  doseAmount: number,
  params: DrugParams
): number => {
  // No effect before dose time
  if (elapsedMinutes < 0) return 0;
  
  // Calculate key time points in the trapezoid model
  const peakTime = params.timeToPeak;
  const plateauDuration = params.totalDuration * 0.2; // 20% of total duration
  const plateauEndTime = peakTime + plateauDuration;
  const totalDuration = params.totalDuration;

  // Effect level based on where we are in the timeline
  if (elapsedMinutes < peakTime) {
    // Linear increase to peak
    return (elapsedMinutes / peakTime) * doseAmount;
  } else if (elapsedMinutes < plateauEndTime) {
    // Plateau at peak effect
    return doseAmount;
  } else if (elapsedMinutes < totalDuration) {
    // Linear decrease from peak to zero
    const remainingTime = totalDuration - elapsedMinutes;
    const decreaseTime = totalDuration - plateauEndTime;
    return (remainingTime / decreaseTime) * doseAmount;
  } else {
    // After total duration, no effect
    return 0;
  }
};

// Generate time points from first dose to last effect + buffer
const generateTimePoints = (
  doses: Dose[],
  drugParams: Record<string, DrugParams>
): { timePoints: number[], firstDoseTime: number } => {
  if (!doses.length) return { timePoints: [], firstDoseTime: 0 };

  // Find earliest and latest times
  const firstDoseTime = Math.min(...doses.map(dose => dose.timestamp));
  const lastDoseTime = Math.max(...doses.map(dose => dose.timestamp));
  
  // Find the longest duration to determine when effects completely end
  const longestDuration = Math.max(
    ...doses.map(dose => {
      const params = drugParams[dose.drugId];
      return params ? params.totalDuration : 0;
    })
  );
  
  // Calculate end time (last dose + longest duration) with 30 minute buffer
  const endTimeMinutes = 
    (lastDoseTime - firstDoseTime) / 60000 + longestDuration + 30;
  
  // Generate time points - step size depends on if any drugs are fast-acting
  // Default to 5 minutes, use smaller step for fast-acting drugs
  const anyFastActingDrugs = Object.values(drugParams).some(param => param.isFastActing);
  const timeStep = anyFastActingDrugs ? 2 : 5; // 2 minutes for fast-acting drugs
  
  const timePoints: number[] = [];
  for (let t = 0; t <= endTimeMinutes; t += timeStep) {
    timePoints.push(t);
  }
  
  return { timePoints, firstDoseTime };
};

// Function to generate formatted time label (HH:MM) from minutes and first dose time
const formatTimeLabel = (minutesElapsed: number, firstDoseTime: number): string => {
  const time = new Date(firstDoseTime + minutesElapsed * 60000);
  return time.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

// Generate time ticks for X-axis based on round times
const generateTimeTicks = (
  dataPoints: DataPoint[], 
  isFastActing: boolean, 
  totalDuration: number
): number[] => {
  if (dataPoints.length === 0) return [];
  
  const firstTime = dataPoints[0].time;
  const lastTime = dataPoints[dataPoints.length - 1].time;
  const firstTimePoint = dataPoints[0].timeLabel;
  
  // Get the actual date from the first time point
  const [hours, minutes] = firstTimePoint.split(':').map(Number);
  
  // Calculate the nearest half-hour time before the first dose
  // e.g., if first dose is at 15:34, start at 15:00 or 15:30 depending on which is closer
  const roundToNearest30Min = (h: number, m: number): {hours: number, minutes: number} => {
    if (m < 15) {
      // Round down to the hour (e.g., 15:08 -> 15:00)
      return { hours: h, minutes: 0 };
    } else if (m >= 15 && m < 45) {
      // Round to half hour (e.g., 15:34 -> 15:30)
      return { hours: h, minutes: 30 };
    } else {
      // Round up to next hour (e.g., 15:46 -> 16:00)
      return { hours: h + 1, minutes: 0 };
    }
  };
  
  const roundedTime = roundToNearest30Min(hours, minutes);
  
  // Standard 30-minute intervals
  const interval = 30;
  
  // Calculate the offset from the first time point to our rounded starting time
  const firstDataPointTime = new Date();
  firstDataPointTime.setHours(hours, minutes, 0, 0);
  
  const roundedStartTime = new Date();
  roundedStartTime.setHours(roundedTime.hours, roundedTime.minutes, 0, 0);
  
  // Convert time difference to minutes (may be negative if rounded time is before first point)
  const offsetMinutes = (roundedStartTime.getTime() - firstDataPointTime.getTime()) / 60000;
  
  // Generate tick values at 30-minute intervals starting from rounded time
  const ticks: number[] = [];
  let currentMinute = firstTime + offsetMinutes;
  
  // Generate ticks at 30-minute intervals
  while (currentMinute <= lastTime) {
    ticks.push(currentMinute);
    currentMinute += interval;
  }
  
  return ticks;
};

// Function to format timestamp for display in line names
const formatTimestampShort = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

// Function to generate data points for each time step, summing effects of all doses
const generatePlotData = (
  doses: Dose[],
  drugParams: Record<string, DrugParams>
): DrugEffectData[] => {
  if (!doses.length) return [];

  // Group doses by drug
  const dosesByDrug: Record<string, Dose[]> = {};
  doses.forEach(dose => {
    if (!dosesByDrug[dose.drugId]) {
      dosesByDrug[dose.drugId] = [];
    }
    dosesByDrug[dose.drugId].push(dose);
  });

  // Generate time points
  const { timePoints, firstDoseTime } = generateTimePoints(doses, drugParams);
  if (timePoints.length === 0) return [];

  // Calculate effects for each drug at each time point
  const drugEffects: DrugEffectData[] = Object.keys(dosesByDrug).map(drugId => {
    const drugDoses = dosesByDrug[drugId];
    const params = drugParams[drugId];
    const unit = drugDoses[0]?.unit || '';
    
    // Calculate effect at each time point
    const dataPoints = timePoints.map(minutes => {
      // Start with timestamp label
      const point: DataPoint = {
        time: minutes,
        timeLabel: formatTimeLabel(minutes, firstDoseTime)
      };
      
      // Calculate effect for each individual dose and store it
      let totalEffect = 0;
      drugDoses.forEach(dose => {
        const doseElapsedMinutes = minutes - (dose.timestamp - firstDoseTime) / 60000;
        const effect = calculateDoseEffect(doseElapsedMinutes, dose.dosage, params);
        
        // Add individual dose effect to the data point with a unique key (drugId_doseId)
        const doseKey = `${drugId}_${dose.id}`;
        point[doseKey] = effect;
        
        // Add to total effect
        totalEffect += effect;
      });
      
      // Add sum of all doses for this drug
      point[drugId] = totalEffect;
      return point;
    });
    
    // Find max effect for scaling
    const maxEffect = Math.max(...dataPoints.map(point => point[drugId] as number));
    
    return {
      drugId,
      drugName: params.drugName || drugId,
      color: params.color || '#8884d8',
      maxEffect,
      unit: unit,
      isFastActing: params.isFastActing || false,
      dataPoints,
      doses: drugDoses // Include the doses for rendering individual lines
    };
  });

  return drugEffects;
};

// Custom tick component for X-axis to ensure time labels display properly
const CustomXAxisTick = (props: any) => {
  const { x, y, payload, dataPoints, isWideScreen } = props;
  
  // Find the matching data point for this tick
  const time = payload.value;
  const point = dataPoints.find((p: DataPoint) => Math.abs(p.time - time) < 0.1);
  
  // Use the timeLabel from the matching point
  const label = point ? point.timeLabel : '';
  
  // Skip rendering some labels on smaller screens to avoid overcrowding
  // Only show every other label on mobile
  if (!isWideScreen && payload.index % 2 !== 0) {
    return null;
  }
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        textAnchor="middle"
        fill="#9ca3af"
        fontSize={12}
      >
        {label}
      </text>
    </g>
  );
};

// Custom vertical tick component for X-axis that places labels below the chart
const CustomVerticalTick = (props: any) => {
  const { x, y, payload, index } = props;
  
  // Convert minutes to hours
  const timeHours = Math.floor(payload.value / 60);
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={5}
        textAnchor="middle"
        fill="#9ca3af"
        fontSize={10}
      >
        {timeHours > 0 ? `${timeHours}` : '0'}
      </text>
    </g>
  );
};

interface EffectPlotProps {
  doses: Dose[];
}

const EffectPlot: React.FC<EffectPlotProps> = ({ doses }) => {
  const { drugs } = useDrugs();
  // Add state to track window width
  const [isWideScreen, setIsWideScreen] = React.useState(window.innerWidth >= 768);
  // Add state to track current time
  const [currentTime, setCurrentTime] = React.useState(new Date().getTime());
  
  // Listen for window resize events
  React.useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth >= 768);
    };
    
    // Set initial value
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update current time every second
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Create drug parameters map from the doses and drug data
  const drugParamsMap = useMemo(() => {
    const params: Record<string, DrugParams> = {};
    
    doses.forEach(dose => {
      if (params[dose.drugId]) return; // Skip if already processed
      
      const drug = getDrugById(dose.drugId, drugs);
      if (!drug) return;
      
      const fastActing = isFastActingDrug(dose.drugId);
      
      params[dose.drugId] = {
        timeToPeak: drug.peakTime || 60, // Default to 60 minutes if not specified
        totalDuration: drug.duration || 240, // Default to 240 minutes if not specified
        drugName: drug.name,
        color: getDrugColor(dose.drugId).replace('bg-', 'text-'),
        unit: dose.unit, // Store the unit for the drug
        isFastActing: fastActing // Flag if it's a fast-acting drug
      };
    });
    
    return params;
  }, [doses, drugs]);
  
  // Generate plot data
  const effectData = useMemo(() => 
    generatePlotData(doses, drugParamsMap), 
    [doses, drugParamsMap]
  );

  if (!doses.length) {
    return (
      <div className="text-center py-8 text-gray-400">
        No consumption data to visualize.
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {effectData.map(drugEffect => {
        // Convert color class to hex color
        const colorClass = drugEffect.color.replace('text-', '');
        let lineColor = '#8884d8'; // Default purple
        
        // Map Tailwind color classes to hex values
        if (colorClass.includes('red')) lineColor = '#ef4444';
        else if (colorClass.includes('blue')) lineColor = '#3b82f6';
        else if (colorClass.includes('green')) lineColor = '#10b981';
        else if (colorClass.includes('emerald')) lineColor = '#10b981';
        else if (colorClass.includes('purple')) lineColor = '#8b5cf6';
        else if (colorClass.includes('violet')) lineColor = '#8b5cf6';
        else if (colorClass.includes('pink')) lineColor = '#ec4899';
        else if (colorClass.includes('teal')) lineColor = '#14b8a6';

        // Generate a dynamic array of hour ticks that covers the entire range of data
        const generateHourTicks = () => {
          // Find the maximum time value in the data points
          const maxTime = Math.max(...drugEffect.dataPoints.map(point => point.time));
          // Convert to hours and round up to the next hour to ensure we cover the full range
          const maxHours = Math.ceil(maxTime / 60);
          
          // Generate hour ticks from 0 to maxHours
          const ticks = [];
          for (let hour = 0; hour <= maxHours; hour++) {
            ticks.push(hour * 60); // Convert hours to minutes for the tick values
          }
          return ticks;
        };
        
        const hourTicks = generateHourTicks();
        
        // Create a lighter color version for individual dose lines
        const lighterColor = lineColor + '80'; // Add 50% transparency
        
        // Calculate current time point for the graph (minutes since first dose)
        const firstDoseTime = Math.min(...doses.map(dose => dose.timestamp));
        const currentTimePoint = (currentTime - firstDoseTime) / 60000; // Convert to minutes

        // Find closest data point to current time
        const closestPoint = drugEffect.dataPoints.reduce((prev, curr) => {
          return Math.abs(curr.time - currentTimePoint) < Math.abs(prev.time - currentTimePoint) ? curr : prev;
        }, drugEffect.dataPoints[0]);

        // Get y-value for current time (drug effect at current time)
        const currentEffect = closestPoint[drugEffect.drugId] as number;
        
        return (
          <div key={drugEffect.drugId} className="bg-gray-800 p-4 rounded-lg shadow-lg">
            <h4 className="text-lg font-medium text-white mb-3">
              {drugEffect.drugName}
            </h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={drugEffect.dataPoints}
                margin={{ top: 10, right: 20, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time"
                  type="number"
                  stroke="#9ca3af"
                  tick={<CustomVerticalTick />}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  height={20}
                  domain={[0, 'dataMax']}
                  ticks={hourTicks}
                  label={{ 
                    value: 'hr', 
                    position: 'right',
                    offset: -5,
                    dy: 20,
                    style: { fill: '#9ca3af', fontSize: 12 } 
                  }}
                />
                <YAxis 
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af' }}
                  tickLine={{ stroke: '#4b5563' }}
                  domain={[0, Math.ceil(drugEffect.maxEffect * 1.1)]}
                  label={{ 
                    value: drugEffect.unit, 
                    angle: -90, 
                    position: 'top',
                    dx: -10,
                    dy: 10,
                    style: { fill: '#9ca3af', fontSize: 12 } 
                  }}
                />
                <Tooltip 
                  content={<CustomTooltip />} 
                  // Add unit information to the payload
                  formatter={(value: any, name: string, props: any) => {
                    // Only format entries with values > 0 to avoid clutter
                    if (value <= 0) return null;
                    
                    // Extract unit from the drugEffect
                    const unit = drugEffect.unit;
                    
                    // Return formatted value with unit
                    return [value, name, unit];
                  }}
                />
                
                {/* Individual dose lines (dashed and lighter) */}
                {drugEffect.doses.map(dose => {
                  const doseKey = `${drugEffect.drugId}_${dose.id}`;
                  const doseTime = formatTimestampShort(dose.timestamp);
                  return (
                    <Line
                      key={doseKey}
                      type="monotone"
                      dataKey={doseKey}
                      name={`${dose.dosage}${dose.unit}@${doseTime}`}
                      stroke={lighterColor}
                      strokeDasharray="5 5"
                      dot={false}
                      strokeWidth={2}
                      unit={drugEffect.unit}
                      // Hide from legend if multiple doses
                      hide={drugEffect.doses.length > 3} // Only show individual lines if 3 or fewer doses
                    />
                  );
                })}
                
                {/* Sum line (thicker) */}
                <Line
                  type="monotone"
                  dataKey={drugEffect.drugId}
                  name={`Total`}
                  stroke={lineColor}
                  dot={false}
                  strokeWidth={3}
                  unit={drugEffect.unit}
                />

                {/* Reference dot for current time */}
                {currentTimePoint >= 0 && currentTimePoint <= Math.max(...drugEffect.dataPoints.map(p => p.time)) && (
                  <ReferenceDot
                    x={currentTimePoint}
                    y={currentEffect}
                    r={6}
                    fill={lineColor}
                    stroke="white"
                    strokeWidth={2}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
};

export default EffectPlot; 